import { useState, useEffect, useMemo } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { useNavigate, useParams } from 'react-router-dom'
import { 
  House, 
  FileText, 
  Briefcase, 
  TrendUp, 
  ChartBar, 
  Gear,
  Moon,
  Sun,
  List,
  X,
  Plus,
  CurrencyDollar,
  CreditCard,
  SignOut,
  Article,
  ShoppingCart,
  ShieldCheck,
  UsersThree,
  ChatCircleText,
  Envelope,
} from '@phosphor-icons/react'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { cn } from '@/lib/utils'
import { LocaleSelector } from '@/components/LocaleSelector'
import { useTranslation } from 'react-i18next'
import { PinDayLogo, PinDayLogoLight } from '@/components/ui/PinDayLogo'
import { GlobalSearchDialog } from './GlobalSearchDialog'
import { TenantSwitch2FADialog, type SwitchTwoFactorPhase } from '@/components/auth/TenantSwitch2FADialog'
import { api } from '@/lib/api'
import { useAIUsage } from '@/hooks/useAIGenerate'
import { usePlanLimits } from '@/hooks/useFeatureGate'
import { isUnlimitedLimit } from '@/lib/plan-entitlements'
import { usePermissions } from '@/contexts/PermissionsContext'
import { NAV_REGISTRY, isNavItemAuthorized, type NavItemId } from '@/lib/navigation'
// import { IntegrationsHealth } from '@/components/dashboard/IntegrationsHealth'

const NAV_ICONS: Record<string, React.ComponentType<{ size?: number; weight?: 'regular' | 'fill' }>> = {
  House,
  FileText,
  ChatCircleText,
  Envelope,
  Briefcase,
  ShoppingCart,
  Article,
  TrendUp,
  ChartBar,
  CreditCard,
  UsersThree,
  ShieldCheck,
  Gear,
}

// Fixed sidebar order — the registry may hold entries (e.g. "integrations")
// that are not rendered as a standalone sidebar link.
const SIDEBAR_ORDER: NavItemId[] = [
  'dashboard',
  'proposals',
  'follow-ups',
  'booking-requests',
  'packages',
  'addons',
  'contract-templates',
  'earnings',
  'metrics',
  'billing',
  'team',
  'admin',
  'preferences',
]

interface AppShellProps {
  children: React.ReactNode
  currentPage: 'dashboard' | 'proposals' | 'follow-ups' | 'booking-requests' | 'packages' | 'addons' | 'contract-templates' | 'earnings' | 'metrics' | 'preferences' | 'pricing' | 'billing' | 'recurring-plans' | 'integrations' | 'admin' | 'team'
  onCreateProposal?: () => void
}

interface NavItem {
  id: 'dashboard' | 'proposals' | 'follow-ups' | 'booking-requests' | 'packages' | 'addons' | 'contract-templates' | 'earnings' | 'metrics' | 'preferences' | 'pricing' | 'billing' | 'recurring-plans' | 'integrations' | 'admin' | 'team'
  label: string
  icon: React.ComponentType<{ size?: number; weight?: 'regular' | 'fill' }>
}

type SidebarLimitItem = {
  id: 'events' | 'ai' | 'services'
  label: string
  used: number
  limit: number
  percentage: number
}

export function AppShell({ children, currentPage, onCreateProposal }: AppShellProps) {
  const [isDark, setIsDark] = useState(() => {
    const saved = localStorage.getItem('dark-mode')
    return saved === 'true'
  })
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const { user, plan, logout, memberships, switchTenant } = useAuth()
  const { role, can } = usePermissions()
  const [isSwitchingTenant, setIsSwitchingTenant] = useState(false)
  const [switchTwoFactor, setSwitchTwoFactor] = useState<SwitchTwoFactorPhase | null>(null)
  const { t } = useTranslation()
  const { data: aiUsage } = useAIUsage()
  const { maxPackages } = usePlanLimits()
  const navigate = useNavigate()
  const { locale } = useParams<{ locale: string }>()
  const currentLocale = locale || 'en'
  const userId = user?.id
  const showUpgradeChip = plan === 'free' || plan === 'starter'
  const showHeaderUpgradeChip = showUpgradeChip && currentPage !== 'dashboard'
  const [eventUsage, setEventUsage] = useState<{ used: number; limit: number } | null>(null)
  const [packagesCount, setPackagesCount] = useState<number | null>(null)

  // Internal navigation that always uses current locale from URL
  const handleInternalNavigate = (page: string) => {
    navigate(`/${currentLocale}/${page}`)
  }

  useEffect(() => {
    localStorage.setItem('dark-mode', String(isDark))
    if (isDark) {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
  }, [isDark])

  const toggleDarkMode = () => {
    setIsDark(!isDark)
  }

  useEffect(() => {
    let mounted = true

    const loadUsage = async () => {
      if (!userId) return

      try {
        const [usage, packages] = await Promise.all([
          api.getUsage(),
          api.getPackages().catch(() => null),
        ])

        if (mounted) {
          setEventUsage({
            used: usage.used ?? 0,
            limit: usage.limit ?? 0,
          })
          if (packages) {
            setPackagesCount(packages.length)
          }
        }
      } catch {
        if (mounted) {
          setEventUsage(null)
          setPackagesCount(null)
        }
      }
    }

    loadUsage()

    return () => {
      mounted = false
    }
  }, [currentPage, userId])

  const usageItems = useMemo<SidebarLimitItem[]>(() => {
    const items: SidebarLimitItem[] = []

    if (eventUsage && eventUsage.limit > 0 && !isUnlimitedLimit(eventUsage.limit)) {
      items.push({
        id: 'events',
        label: t('dashboard:usage.eventsLabel'),
        used: eventUsage.used,
        limit: eventUsage.limit,
        percentage: Math.min((eventUsage.used / eventUsage.limit) * 100, 100),
      })
    }

    if (aiUsage && aiUsage.limit > 0 && !isUnlimitedLimit(aiUsage.limit)) {
      items.push({
        id: 'ai',
        label: t('dashboard:usage.aiLabel'),
        used: aiUsage.used,
        limit: aiUsage.limit,
        percentage: Math.min((aiUsage.used / aiUsage.limit) * 100, 100),
      })
    }

    if (packagesCount !== null && maxPackages > 0 && !isUnlimitedLimit(maxPackages)) {
      items.push({
        id: 'services',
        label: t('dashboard:usage.servicesLabel'),
        used: packagesCount,
        limit: maxPackages,
        percentage: Math.min((packagesCount / maxPackages) * 100, 100),
      })
    }

    return items
  }, [aiUsage, eventUsage, maxPackages, packagesCount, t])

  const renderPlanUsageCard = () => (
    <div className="rounded-xl border border-[var(--pd-border)] bg-[var(--pd-muted)]/35 p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-[var(--pd-foreground)]/65">
          {t('dashboard:usage.sidebarTitle')}
        </p>
        {plan === 'studio' ? (
          <span className="rounded-full border border-[var(--pd-primary)]/20 bg-[var(--pd-primary)]/10 px-2 py-0.5 text-[10px] font-semibold text-[var(--pd-primary)]">
            {t('dashboard:conversion.studio.unlimited')}
          </span>
        ) : null}
      </div>

      {usageItems.length > 0 ? (
        <div className="space-y-2">
          {usageItems.map((item) => (
            <div key={item.id}>
              <div className="mb-1 flex items-center justify-between text-[11px]">
                <span className="text-[var(--pd-foreground)]/70">{item.label}</span>
                <span className="font-medium text-[var(--pd-foreground)]/65">
                  {item.used}/{item.limit}
                </span>
              </div>
              <div className="h-1.5 w-full rounded-full bg-[var(--pd-muted)]">
                <div
                  className={cn(
                    'h-full rounded-full transition-all duration-300',
                    item.id === 'events'
                      ? 'bg-[var(--pd-primary)]/55'
                      : item.id === 'services'
                        ? 'bg-[var(--pd-info)]/65'
                        : 'bg-[var(--pd-accent)]/60'
                  )}
                  style={{ width: `${item.percentage}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-xs text-[var(--pd-foreground)]/60">
          {t('dashboard:usage.noTrackedLimits')}
        </p>
      )}
    </div>
  )

  const navItems: NavItem[] = useMemo(() => {
    return SIDEBAR_ORDER.reduce<NavItem[]>((items, id) => {
      const requirement = NAV_REGISTRY.find((entry) => entry.id === id)
      if (!requirement) return items
      if (!isNavItemAuthorized(requirement, { plan, role: role ?? user?.role ?? null, can })) return items

      const Icon = (requirement.icon && NAV_ICONS[requirement.icon]) || House
      const label =
        id === 'team' && role === 'member' ? t('team:nav.mySpace') : t(requirement.labelKey)

      items.push({ id, label, icon: Icon })
      return items
    }, [])
  }, [plan, role, can, user?.role, t])

  const currentPageLabel = navItems.find((i) => i.id === currentPage)?.label

  const getUserInitials = (name: string | undefined) => {
    if (!name) return 'U'
    return name.substring(0, 2).toUpperCase()
  }

  const handleNavigation = (page: NavItem['id']) => {
    handleInternalNavigate(page)
    setIsMobileMenuOpen(false)
  }

  const handleUpgradeNavigation = () => {
    navigate(`/${currentLocale}/pricing?from=dashboard_shell_chip`)
  }

  const handleLogout = () => {
    const targetLocale = currentLocale
    logout()
    setIsMobileMenuOpen(false)
    navigate(`/${targetLocale}/login`, { replace: true })
  }

  const finishSwitchNavigation = () => {
    setIsMobileMenuOpen(false)
    navigate(`/${currentLocale}/dashboard`, { replace: true })
    window.location.reload()
  }

  const handleSwitchTenant = async (tenantId: string) => {
    if (isSwitchingTenant) return
    setIsSwitchingTenant(true)
    try {
      const result = await switchTenant(tenantId)
      if (result.status === 'authenticated') {
        finishSwitchNavigation()
      } else if (result.status === 'two_factor_required') {
        // Target tenant requires a 2FA step-up before the switch completes.
        setSwitchTwoFactor({ kind: 'challenge', challengeToken: result.challengeToken })
      } else if (result.status === 'two_factor_setup_required') {
        setSwitchTwoFactor({ kind: 'enroll', setupToken: result.setupToken, orgName: result.tenant?.name })
      }
    } catch (error) {
      console.error('Failed to switch workspace:', error)
    } finally {
      setIsSwitchingTenant(false)
    }
  }

  return (
    <div className="min-h-screen w-full overflow-x-hidden bg-[var(--pd-bg)]">
      <div className="hidden lg:flex lg:w-64 lg:flex-col lg:fixed lg:inset-y-0">
        <div className="flex flex-col flex-grow border-r border-[var(--pd-border)] bg-[var(--pd-card)] overflow-y-auto">
          <div className="flex items-center flex-shrink-0 px-4 py-4 border-b border-[var(--pd-border)]">
            {isDark ? <PinDayLogoLight size="sm" /> : <PinDayLogo size="sm" />}
          </div>

          <div className="flex-1 flex flex-col pt-5 pb-4 px-3">
            <nav className="flex-1 space-y-1">
              {navItems.map((item) => {
                const Icon = item.icon
                const isActive = currentPage === item.id
                return (
                  <button
                    key={item.id}
                    onClick={() => handleNavigation(item.id)}
                    className={cn(
                      'group flex items-center px-3 py-2.5 text-sm font-medium rounded-xl w-full transition-all duration-200',
                      isActive
                        ? 'bg-[var(--pd-primary-bg)] text-[var(--pd-primary-text)] shadow-sm'
                        : 'text-[var(--pd-foreground)] hover:bg-[var(--pd-muted)]'
                    )}
                  >
                    <span className={cn(
                      'mr-3',
                      isActive ? 'text-[var(--pd-primary-text)]' : 'text-[var(--pd-primary-600)]'
                    )}>
                      <Icon 
                        size={20} 
                        weight={isActive ? 'fill' : 'regular'}
                      />
                    </span>
                    {item.label}
                  </button>
                )
              })}
            </nav>

            <div className="mt-6 pt-6 border-t border-[var(--pd-border)] space-y-3">
              {renderPlanUsageCard()}

              <button
                onClick={() => handleNavigation('pricing')}
                className={cn(
                  'group flex items-center px-3 py-2.5 text-sm font-medium rounded-xl w-full transition-all duration-200',
                  currentPage === 'pricing'
                    ? 'bg-[var(--pd-accent-bg)] text-[var(--pd-accent-text)] shadow-sm'
                    : 'text-[var(--pd-accent)] hover:bg-[var(--pd-beige)] border border-[var(--pd-accent)]/20'
                )}
              >
                <CurrencyDollar 
                  size={20} 
                  weight={currentPage === 'pricing' ? 'fill' : 'regular'}
                  className="mr-3"
                />
                {t('billing:pricing.title')}
              </button>

              <button
                onClick={toggleDarkMode}
                className="group flex items-center px-3 py-2.5 text-sm font-medium rounded-xl w-full text-[var(--pd-foreground)] hover:bg-[var(--pd-muted)] transition-all duration-200"
              >
                {isDark ? (
                  <Sun size={20} className="mr-3 text-[var(--pd-primary-600)]" />
                ) : (
                  <Moon size={20} className="mr-3 text-[var(--pd-primary-600)]" />
                )}
                {isDark ? t('common:lightMode') : t('common:darkMode')}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="lg:pl-64 flex w-full min-w-0 flex-col flex-1">
        <div className="sticky top-0 z-10 flex-shrink-0 flex h-16 bg-[var(--pd-card)] border-b border-[var(--pd-border)] shadow-[var(--pd-shadow-sm)]">
          <button
            type="button"
            data-testid="mobile-menu-trigger"
            className="px-4 border-r border-[var(--pd-border)] text-[var(--pd-foreground)] focus:outline-none focus:ring-2 focus:ring-inset focus:ring-[var(--pd-ring)] lg:hidden"
            onClick={() => setIsMobileMenuOpen(true)}
          >
            <List size={24} />
          </button>

          <div className="flex min-w-0 flex-1 items-center justify-between px-4 lg:px-6">
            <div className="flex min-w-0 flex-1 items-center gap-3 lg:hidden">
              <PinDayLogo size="sm" />
              {currentPageLabel ? (
                <span className="min-w-0 truncate text-sm font-semibold text-[var(--pd-foreground)]">
                  {currentPageLabel}
                </span>
              ) : null}
            </div>
            
            {/* Ações à direita */}
            <div className="ml-auto flex min-w-0 items-center gap-1.5 sm:gap-2 lg:gap-3">
              {/* Busca global */}
              <GlobalSearchDialog className="w-full max-w-[14rem] xl:max-w-sm" />
              
              <div className="h-6 w-px bg-[var(--pd-border)] hidden xl:block" />
              
              <LocaleSelector />

              {showHeaderUpgradeChip ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleUpgradeNavigation}
                  aria-label={t('dashboard:conversion.chipAriaLabel')}
                  className="hidden sm:inline-flex lg:hidden rounded-full border-[var(--pd-accent)]/25 bg-[var(--pd-accent)]/10 px-3 text-[var(--pd-accent)] hover:bg-[var(--pd-accent)]/15"
                >
                  <CurrencyDollar size={14} weight="bold" className="mr-1" />
                  {t('dashboard:conversion.chipLabel')}
                </Button>
              ) : null}
              
              <div className="h-6 w-px bg-[var(--pd-border)] hidden xl:block" />
              
              {currentPage === 'dashboard' || currentPage === 'proposals' ? (
                <>
                  <Button
                    onClick={onCreateProposal}
                    size="icon"
                    className="hidden lg:inline-flex 2xl:hidden bg-[var(--pd-accent)] hover:bg-[var(--pd-accent)]/90 text-white rounded-xl shadow-sm transition-all duration-200"
                    title={t('common:actions.createProposal')}
                  >
                    <Plus size={20} weight="bold" />
                  </Button>
                  <Button
                    onClick={onCreateProposal}
                    className="hidden 2xl:inline-flex bg-[var(--pd-accent)] hover:bg-[var(--pd-accent)]/90 text-white rounded-xl px-4 py-2 font-medium shadow-sm transition-all duration-200"
                  >
                    <Plus size={20} className="mr-2" weight="bold" />
                    <span>{t('common:actions.createProposal')}</span>
                  </Button>
                </>
              ) : null}

              {user && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="ghost" 
                      className="relative h-10 gap-2 pl-2 pr-3 rounded-xl hover:bg-[var(--pd-muted)] transition-colors"
                    >
                      <Avatar className="h-8 w-8 ring-2 ring-[var(--pd-border)]">
                        <AvatarImage src={user.avatarUrl} alt={user.name} />
                        <AvatarFallback className="bg-[var(--pd-primary)] text-white text-xs font-semibold">
                          {getUserInitials(user.name)}
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-sm font-medium text-[var(--pd-foreground)] max-w-[100px] truncate hidden 2xl:inline">
                        {user.name}
                      </span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-56 bg-[var(--pd-card)] border-[var(--pd-border)] rounded-xl shadow-[var(--pd-shadow)]" align="end" sideOffset={8}>
                    <DropdownMenuLabel className="font-normal px-2 py-2">
                      <div className="flex flex-col gap-1">
                        <p className="text-sm font-semibold text-[var(--pd-foreground)]">{user.name}</p>
                        <p className="text-xs text-[var(--pd-foreground)]/60">{user.email}</p>
                      </div>
                    </DropdownMenuLabel>
                    {memberships.length > 1 && (
                      <>
                        <DropdownMenuSeparator className="bg-[var(--pd-border)]" />
                        <DropdownMenuLabel className="px-2 py-1 text-xs font-medium text-[var(--pd-foreground)]/50">
                          {t('workspace:switcher.label')}
                        </DropdownMenuLabel>
                        {memberships.map((membership) => (
                          <DropdownMenuItem
                            key={membership.tenantId}
                            onClick={() => handleSwitchTenant(membership.tenantId)}
                            disabled={isSwitchingTenant || membership.isActive}
                            className="gap-3 px-2 py-2 rounded-lg cursor-pointer"
                          >
                            <UsersThree size={18} />
                            <span className="text-sm truncate flex-1">{membership.tenantName}</span>
                            {membership.isActive && (
                              <span className="text-xs text-[var(--pd-foreground)]/50">
                                {t('workspace:switcher.current')}
                              </span>
                            )}
                          </DropdownMenuItem>
                        ))}
                      </>
                    )}
                    <DropdownMenuSeparator className="bg-[var(--pd-border)]" />
                    <DropdownMenuItem
                      onClick={() => handleNavigation('preferences')}
                      className="gap-3 px-2 py-2 rounded-lg cursor-pointer"
                    >
                      <Gear size={18} />
                      <span className="text-sm">{t('common:navigation.preferences')}</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => handleNavigation('pricing')}
                      className="gap-3 px-2 py-2 rounded-lg cursor-pointer"
                    >
                      <CurrencyDollar size={18} />
                      <span className="text-sm">{t('billing:pricing.title')}</span>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-[var(--pd-border)]" />
                    <DropdownMenuItem
                      onClick={handleLogout}
                      className="gap-3 px-2 py-2 rounded-lg cursor-pointer text-[var(--pd-danger)]"
                    >
                      <SignOut size={18} />
                      <span className="text-sm">{t('common:navigation.logout')}</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>
        </div>

        <main className="flex-1 min-w-0">
          {children}
        </main>
      </div>

      <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
        <SheetContent
          side="left"
          hideDefaultClose
          className="w-80 p-0 bg-[var(--pd-card)] border-[var(--pd-border)]"
        >
          <SheetHeader className="sr-only">
            <SheetTitle>Navigation Menu</SheetTitle>
            <SheetDescription>Main navigation menu for mobile devices.</SheetDescription>
          </SheetHeader>
          <div className="flex flex-col h-full">
            <div className="flex items-center justify-between px-6 py-6 border-b border-[var(--pd-border)]">
              <PinDayLogo size="md" />
              <button
                type="button"
                aria-label="Close menu"
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-[var(--pd-foreground)] hover:bg-[var(--pd-muted)] p-2 rounded-lg transition-colors"
              >
                <X size={20} weight="bold" />
              </button>
            </div>

            {user && (
              <div className="flex items-center gap-3 px-6 py-4 border-b border-[var(--pd-border)] bg-[var(--pd-muted)]">
                <Avatar className="h-10 w-10 ring-2 ring-[var(--pd-border)]">
                  <AvatarImage src={user.avatarUrl} alt={user.name} />
                  <AvatarFallback className="bg-[var(--pd-primary)] text-white font-semibold">
                    {getUserInitials(user.name)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex flex-col gap-1 flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[var(--pd-foreground)] truncate">{user.name}</p>
                  <p className="text-xs text-[var(--pd-foreground)]/60 truncate">{user.email}</p>
                </div>
              </div>
            )}

            {memberships.length > 1 && (
              <div className="flex-shrink-0 px-3 py-3 border-b border-[var(--pd-border)] space-y-1">
                <p className="px-3 pb-1 text-xs font-medium text-[var(--pd-foreground)]/50">
                  {t('workspace:switcher.label')}
                </p>
                {memberships.map((membership) => (
                  <button
                    key={membership.tenantId}
                    onClick={() => handleSwitchTenant(membership.tenantId)}
                    disabled={isSwitchingTenant || membership.isActive}
                    className={cn(
                      'flex items-center gap-3 w-full px-3 py-2 text-sm rounded-xl transition-colors',
                      membership.isActive
                        ? 'bg-[var(--pd-primary-bg)] text-[var(--pd-primary-text)]'
                        : 'text-[var(--pd-foreground)] hover:bg-[var(--pd-muted)]',
                    )}
                  >
                    <UsersThree size={18} />
                    <span className="truncate flex-1 text-left">{membership.tenantName}</span>
                    {membership.isActive && (
                      <span className="text-xs text-[var(--pd-foreground)]/50">
                        {t('workspace:switcher.current')}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}

            <nav className="flex-1 min-h-0 overflow-y-auto px-3 py-4 space-y-1">
              {navItems.map((item) => {
                const Icon = item.icon
                const isActive = currentPage === item.id
                return (
                  <button
                    key={item.id}
                    onClick={() => handleNavigation(item.id)}
                    className={cn(
                      'group flex items-center px-3 py-2.5 text-sm font-medium rounded-xl w-full transition-all duration-200',
                      isActive
                        ? 'bg-[var(--pd-primary-bg)] text-[var(--pd-primary-text)] shadow-sm'
                        : 'text-[var(--pd-foreground)] hover:bg-[var(--pd-muted)]'
                    )}
                  >
                    <span className={cn(
                      'mr-3',
                      isActive ? 'text-[var(--pd-primary-text)]' : 'text-[var(--pd-primary-600)]'
                    )}>
                      <Icon 
                        size={20} 
                        weight={isActive ? 'fill' : 'regular'}
                      />
                    </span>
                    {item.label}
                  </button>
                )
              })}
            </nav>

            <div className="flex-shrink-0 p-3 border-t border-[var(--pd-border)] space-y-3">
              {renderPlanUsageCard()}

              <button
                onClick={() => handleNavigation('pricing')}
                className={cn(
                  'group flex items-center px-3 py-2.5 text-sm font-medium rounded-xl w-full transition-all duration-200',
                  currentPage === 'pricing'
                    ? 'bg-[var(--pd-accent-bg)] text-[var(--pd-accent-text)] shadow-sm'
                    : 'text-[var(--pd-accent)] hover:bg-[var(--pd-beige)] border border-[var(--pd-accent)]/20'
                )}
              >
                <CurrencyDollar
                  size={20}
                  weight={currentPage === 'pricing' ? 'fill' : 'regular'}
                  className="mr-3"
                />
                {t('billing:pricing.title')}
              </button>

              <LocaleSelector variant="inline" />

              <button
                onClick={toggleDarkMode}
                className="group flex items-center px-3 py-2.5 text-sm font-medium rounded-xl w-full text-[var(--pd-foreground)] hover:bg-[var(--pd-muted)] transition-all duration-200"
              >
                {isDark ? (
                  <Sun size={20} className="mr-3 text-[var(--pd-primary-600)]" />
                ) : (
                  <Moon size={20} className="mr-3 text-[var(--pd-primary-600)]" />
                )}
                {isDark ? t('common:lightMode') : t('common:darkMode')}
              </button>

              {user && (
                <button
                  onClick={handleLogout}
                  className="group flex items-center px-3 py-2.5 text-sm font-medium rounded-xl w-full border-t border-[var(--pd-border)] pt-4 text-[var(--pd-danger)] hover:bg-[var(--pd-danger)]/10 transition-all duration-200"
                >
                  <SignOut size={20} className="mr-3" />
                  {t('common:navigation.logout')}
                </button>
              )}
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <TenantSwitch2FADialog
        phase={switchTwoFactor}
        onAuthenticated={() => {
          setSwitchTwoFactor(null)
          finishSwitchNavigation()
        }}
        onCancel={() => setSwitchTwoFactor(null)}
      />
    </div>
  )
}
